import React, { useCallback, useEffect, useRef, useState } from 'react'
import { fmt } from '../api.js'
import { portal } from '../portalApi.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 매출 — 원장은 하나(이카운트 매출줄), 보는 기준(날짜 축)만 셋이다.
//
//   결제일      이카운트가 들고 있는 날짜. 내부 매출관리 기준.
//   회계마감일  판매처 부가세자료의 매출일을 주문번호로 매칭해 입힌 날짜. 결산에 들어가는 축.
//   배송일      사내 배송 시스템에서 주문번호로 받아 입힌 날짜.
//
// 그래서 같은 8월을 세 가지로 볼 수 있고, 그 차이(월말 결제분이 익월 회계·배송으로 넘어간 것)가
// 곧 이 화면의 쓸모다. 확정은 결산 기준인 회계마감 축으로만 잠근다.

const TABS = [
  { k: 'summary', label: '요약' },
  { k: 'ledger', label: '원장' },
  { k: 'reconcile', label: '대사' },
  { k: 'import', label: '가져오기' },
]

const SUM_COLS = ['건수', '수량', '공급가액', '부가세', '합계금액']
const STATE_STYLE = { 매칭: 'ok', 금액불일치: 'warn', 미매칭: 'muted' }

const pct = (part, whole) => (whole ? Math.max(0, Math.min(100, (part / whole) * 100)) : 0)
const num = (v, c) => fmt(v, c === '수량' ? 2 : 0)

function SumTable({ label, field, rows, total }) {
  return (
    <table>
      <thead>
        <tr>
          <th className="l">{label}</th>
          {SUM_COLS.map((c) => <th key={c}>{c}</th>)}
          <th style={{ width: 150 }}>비중(공급가액)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={String(r[field])}>
            <td className="l">{r[field]}</td>
            {SUM_COLS.map((c) => <td key={c}>{num(r[c], c)}</td>)}
            <td><div className="bar"><i style={{ width: `${pct(r.공급가액, total.공급가액)}%` }} /></div></td>
          </tr>
        ))}
        <tr className="total">
          <td className="l">합계</td>
          {SUM_COLS.map((c) => <td key={c}>{num(total[c], c)}</td>)}
          <td />
        </tr>
      </tbody>
    </table>
  )
}

// 축 전환 — 어느 탭에서든 위에 붙는다
function AxisPicker({ axes, axis, onChange }) {
  return (
    <div className="seg no-print" style={{ marginBottom: 10 }}>
      {axes.map((a) => (
        <label key={a.key} className={axis === a.key ? 'on' : ''} title={a.note}>
          <input type="radio" name="sales-axis" checked={axis === a.key}
            onChange={() => onChange(a.key)} />
          {a.label}
        </label>
      ))}
    </div>
  )
}

// ---------------- 요약 ----------------

function SummaryPane({ month, axes, axis, data, canClose, onChanged, onError }) {
  const [busy, setBusy] = useState(false)
  const s = data.summary
  const total = s.총계
  const close = data.close
  const closed = close.상태 === '확정'
  const compare = data.compare || {}
  const trend = data.trend || []
  const trendMax = Math.max(1, ...trend.map((t) => t.공급가액 || 0))
  const base = compare['결제']?.공급가액 || 0

  const act = async (path, label) => {
    if (!window.confirm(`${month} 매출을 ${label}할까요? (회계마감 기준)`)) return
    setBusy(true)
    try {
      await portal.post(`/api/sales/${path}`, { month })
      onChanged()
    } catch (e) { onError(e.message) } finally { setBusy(false) }
  }

  const download = async () => {
    try { await portal.download(`/api/sales/export?month=${month}&axis=${axis}`) } catch (e) { onError(e.message) }
  }

  return (
    <>
      <div className="panel">
        <h3>기준별 비교 — {month}</h3>
        <div className="cards" style={{ marginBottom: 0 }}>
          {axes.map((a) => {
            const c = compare[a.key] || {}
            const diff = (c.공급가액 || 0) - base
            return (
              <div className="card" key={a.key}
                style={a.key === axis ? { borderColor: 'var(--accent)' } : undefined}>
                <div className="label">{a.label}</div>
                <div className="value">{fmt(c.공급가액)}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  {fmt(c.건수)}줄
                  {a.key !== '결제' && (
                    <> · 결제 대비 <b className={diff === 0 ? 'muted' : (diff > 0 ? 'ok' : 'warn')}>
                      {diff > 0 ? '+' : ''}{fmt(diff)}</b></>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        <div className="hint">
          같은 달을 축만 바꿔 집계한 것입니다. 결제일과 회계마감일의 차이는 월말 결제분이
          익월 부가세자료로 넘어간 금액이고, 배송일과의 차이는 아직 안 나간 주문입니다.
          {s.축날짜없음?.건수 > 0 && (
            <><br /><span className="warn">
              이 달 결제분 중 {fmt(s.축날짜없음.건수)}줄({fmt(s.축날짜없음.공급가액)}원)은
              {' '}{axes.find((a) => a.key === axis)?.label.replace(' 기준', '')}이 아직 없어 위 집계에서 빠져 있습니다.
            </span></>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <b>{month} 회계마감</b>{' '}
            {closed ? <span className="ok">확정됨</span> : <span className="muted">작성중</span>}
            {closed && (
              <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                {close.확정자명 || ''} · {(close.확정일시 || '').slice(0, 10)}
              </span>
            )}
          </div>
          <div className="row no-print">
            <button className="btn secondary" onClick={download}>엑셀 내보내기</button>
            {canClose && !closed && (
              <button className="btn" disabled={busy || !total.건수} onClick={() => act('close', '확정')}>월 확정</button>
            )}
            {canClose && closed && (
              <button className="btn danger" disabled={busy} onClick={() => act('reopen', '확정 해제')}>확정 해제</button>
            )}
          </div>
        </div>
        <div className="hint">
          확정하면 그 달의 회계마감 매출은 잠깁니다 — 가져오기도, 자동 재매칭도, 수기 수정도 그 달은 건드리지 않습니다.
          확정·해제는 관리자만 할 수 있습니다.
        </div>
        {s.매칭상태별?.length > 0 && (
          <div className="row" style={{ gap: 16, marginTop: 6 }}>
            {s.매칭상태별.map((r) => (
              <span key={r.매칭상태} className={STATE_STYLE[r.매칭상태] || ''}>
                {r.매칭상태} {fmt(r.건수)}줄 · {fmt(r.공급가액)}원
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="panel">
        <h3>구분별 <span className="muted" style={{ fontSize: 12 }}>결산 매출요약의 가로축</span></h3>
        {(s.구분별 || []).length === 0
          ? <div className="hint">이 기준으로 잡히는 매출이 없습니다.</div>
          : <SumTable label="구분" field="구분" rows={s.구분별} total={total} />}
      </div>

      <div className="panel">
        <h3>판매처별</h3>
        {s.판매처별.length === 0
          ? <div className="hint">이 기준으로 잡히는 매출이 없습니다.</div>
          : (
            <table>
              <thead>
                <tr>
                  <th className="l">판매처</th><th>구분</th>
                  {SUM_COLS.map((c) => <th key={c}>{c}</th>)}
                  <th style={{ width: 150 }}>비중(공급가액)</th>
                </tr>
              </thead>
              <tbody>
                {s.판매처별.map((r) => (
                  <tr key={r.판매처}>
                    <td className="l">{r.판매처}</td>
                    <td className="c muted">{r.구분}</td>
                    {SUM_COLS.map((c) => <td key={c}>{num(r[c], c)}</td>)}
                    <td><div className="bar"><i style={{ width: `${pct(r.공급가액, total.공급가액)}%` }} /></div></td>
                  </tr>
                ))}
                <tr className="total">
                  <td className="l">합계</td><td />
                  {SUM_COLS.map((c) => <td key={c}>{num(total[c], c)}</td>)}
                  <td />
                </tr>
              </tbody>
            </table>
          )}
        <div className="hint">
          구분이 <b>(미분류)</b>인 판매처는 원장 탭에서 판매처를 눌러 자사몰·유통·온라인 중 하나로 묶어주세요.
        </div>
      </div>

      <div className="panel">
        <h3>품목그룹별</h3>
        {s.품목그룹별.length === 0
          ? <div className="hint">-</div>
          : <SumTable label="품목그룹" field="품목그룹" rows={s.품목그룹별} total={total} />}
        <div className="hint">
          <b>(미분류)</b>가 보이면 원장 탭에서 그 품목의 그룹을 한 번만 정해주세요 —
          같은 품목명은 과거 줄까지 한꺼번에 채워지고, 다음 가져오기부터 자동으로 붙습니다.
        </div>
      </div>

      {s.품목별.length > 0 && (
        <div className="panel">
          <h3>품목별 (상위 {s.품목별.length})</h3>
          <table>
            <thead>
              <tr>
                <th className="l">품목명</th><th className="l">품목그룹</th>
                <th>수량</th><th>공급가액</th><th>합계금액</th><th style={{ width: 150 }}>비중</th>
              </tr>
            </thead>
            <tbody>
              {s.품목별.map((r) => (
                <tr key={`${r.품목명}|${r.품목그룹}`}>
                  <td className="l">{r.품목명}</td>
                  <td className="l">{r.품목그룹 || <span className="muted">(미분류)</span>}</td>
                  <td>{fmt(r.수량, 2)}</td>
                  <td>{fmt(r.공급가액)}</td>
                  <td>{fmt(r.합계금액)}</td>
                  <td><div className="bar"><i style={{ width: `${pct(r.공급가액, total.공급가액)}%` }} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel">
        <h3>최근 12개월 추이 — {axes.find((a) => a.key === axis)?.label}</h3>
        <table>
          <thead>
            <tr><th className="l">월</th><th>줄수</th><th>공급가액</th><th>합계금액</th><th style={{ width: 260 }} /></tr>
          </thead>
          <tbody>
            {trend.map((t) => (
              <tr key={t.월} style={t.월 === month ? { background: 'var(--accent-soft)' } : undefined}>
                <td className="l">{t.월}</td>
                <td>{fmt(t.건수)}</td>
                <td>{fmt(t.공급가액)}</td>
                <td>{fmt(t.합계금액)}</td>
                <td><div className="bar"><i style={{ width: `${pct(t.공급가액, trendMax)}%` }} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ---------------- 원장 ----------------

function LedgerPane({ month, axis, meta, closed, onChanged, onError }) {
  const [channel, setChannel] = useState('')
  const [state, setState] = useState('')
  const [q, setQ] = useState('')
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState({ total: 0, rows: [] })
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState(null) // { id, patch }
  const LIMIT = 100

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const p = new URLSearchParams({ month, axis, limit: String(LIMIT), offset: String(offset) })
      if (channel) p.set('channel', channel)
      if (state) p.set('state', state)
      if (q.trim()) p.set('q', q.trim())
      setData(await portal.get(`/api/sales/rows?${p}`))
    } catch (e) { onError(e.message) } finally { setLoading(false) }
  }, [month, axis, channel, state, q, offset, onError])

  useEffect(() => { load() }, [load])
  useEffect(() => { setOffset(0) }, [month, axis])

  const commitEdit = async () => {
    const { id, patch } = editing
    try {
      const res = await fetch(`/api/sales/rows/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
        credentials: 'same-origin',
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || res.statusText)
      setEditing(null)
      load(); onChanged()
    } catch (e) { onError(e.message) }
  }

  const setGroup = async (row) => {
    const group = window.prompt(`"${row.품목명}"의 품목그룹`, row.품목그룹 || '')
    if (group === null) return
    try {
      await portal.post('/api/sales/item-groups', { 품목명: row.품목명, 품목그룹: group.trim() })
      load(); onChanged()
    } catch (e) { onError(e.message) }
  }

  const setChannelName = async (row) => {
    const source = row.판매처 || row.거래처명
    if (!source) { onError('판매처·거래처명이 없는 줄이라 묶을 수 없습니다'); return }
    const name = window.prompt(`"${source}"을(를) 어느 판매처 이름으로 묶을까요?`, row.판매처 || '')
    if (name === null || !name.trim()) return
    const div = window.prompt(
      `"${name.trim()}"의 결산 구분 (${(meta.divisions || []).join(' / ')})`,
      row.구분 || '')
    if (div === null) return
    try {
      const r = await portal.post('/api/sales/channel-map',
        { 원본명: source, 판매처: name.trim(), 구분: div.trim() })
      if (r.applied) load()
      onChanged()
    } catch (e) { onError(e.message) }
  }

  const pageInfo = `${data.total ? offset + 1 : 0}–${Math.min(offset + LIMIT, data.total)} / ${fmt(data.total)}`
  const channels = (meta.channels?.이카운트 || []).map((c) => c.판매처)

  return (
    <>
      <div className="panel">
        <div className="row">
          <select value={channel} onChange={(e) => { setChannel(e.target.value); setOffset(0) }}>
            <option value="">전체 판매처</option>
            {channels.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={state} onChange={(e) => { setState(e.target.value); setOffset(0) }}>
            <option value="">전체 매칭상태</option>
            {meta.matchStates.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input placeholder="품목명 · 주문번호 · 거래처 · 전표번호" value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setOffset(0); load() } }}
            style={{ width: 260 }} />
          <button className="btn secondary" onClick={() => { setOffset(0); load() }}>검색</button>
          <span className="muted" style={{ marginLeft: 'auto' }}>{pageInfo}</span>
          <button className="btn secondary" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}>이전</button>
          <button className="btn secondary" disabled={offset + LIMIT >= data.total}
            onClick={() => setOffset(offset + LIMIT)}>다음</button>
        </div>
        <div className="hint">
          지금 보고 있는 달은 <b>{meta.axes.find((a) => a.key === axis)?.label}</b>으로 자른 것입니다 —
          축을 바꾸면 같은 달이라도 나오는 줄이 달라집니다.
          {closed && <span className="warn"> {month}은 확정된 달이라 회계마감일을 못 고칩니다.</span>}
        </div>
      </div>

      <div className="panel">
        {loading && <div className="hint">불러오는 중…</div>}
        <table>
          <thead>
            <tr>
              <th className="l">판매처</th><th>주문번호</th><th>전표번호</th>
              <th>결제일</th><th>회계마감일</th><th>배송일</th><th>매칭</th>
              <th className="l">품목명</th><th className="l">품목그룹</th>
              <th>수량</th><th>공급가액</th><th>부가세</th><th>합계금액</th><th />
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 && !loading && (
              <tr><td className="l muted" colSpan={14}>이 조건에 맞는 매출줄이 없습니다.</td></tr>
            )}
            {data.rows.map((r) => {
              const ed = editing?.id === r.id ? editing.patch : null
              const val = (k) => (ed && k in ed ? ed[k] : r[k] ?? '')
              const put = (k, v) => setEditing({ id: r.id, patch: { ...editing.patch, [k]: v } })
              return (
                <tr key={r.id}>
                  <td className="l">
                    <button className="link-cell" onClick={() => setChannelName(r)}
                      title={`구분: ${r.구분 || '(미분류)'}${r.거래처명 ? ` · 이카운트 거래처: ${r.거래처명}` : ''}`}>
                      {r.판매처 || '(미지정)'}
                    </button>
                    {!r.구분 && <span className="muted" style={{ fontSize: 10 }}> 미분류</span>}
                  </td>
                  <td>{r.주문번호 || '-'}</td>
                  <td>{r.전표번호 || '-'}</td>
                  <td>{r.결제일}</td>
                  <td title={r.회계마감일_출처 ? `출처: ${r.회계마감일_출처}` : undefined}>
                    {ed
                      ? <input type="date" className="wide" value={val('회계마감일') || ''}
                          onChange={(e) => put('회계마감일', e.target.value)} />
                      : (
                        <>
                          {r.회계마감일 || '-'}
                          {r.회계마감일_출처 === '수기' && <span className="muted" style={{ fontSize: 10 }}> 수기</span>}
                        </>
                      )}
                  </td>
                  <td title={r.배송일_출처 ? `출처: ${r.배송일_출처}` : undefined}>
                    {ed
                      ? <input type="date" className="wide" value={val('배송일') || ''}
                          onChange={(e) => put('배송일', e.target.value)} />
                      : (r.배송일 || <span className="muted">-</span>)}
                  </td>
                  <td className={`c ${STATE_STYLE[r.매칭상태] || ''}`}>{r.매칭상태}</td>
                  <td className="l">{r.품목명 || '-'}</td>
                  <td className="l">
                    <button className="link-cell" disabled={!r.품목명} onClick={() => setGroup(r)}>
                      {r.품목그룹 || '(미분류)'}
                    </button>
                  </td>
                  <td>{fmt(r.수량, 2)}</td>
                  <td>{fmt(r.공급가액)}</td>
                  <td>{fmt(r.부가세)}</td>
                  <td>{fmt(r.합계금액)}</td>
                  <td className="c">
                    {ed ? (
                      <>
                        <button className="btn tiny" onClick={commitEdit}>저장</button>{' '}
                        <button className="btn tiny secondary" onClick={() => setEditing(null)}>취소</button>
                      </>
                    ) : (
                      <button className="btn tiny secondary" disabled={closed}
                        onClick={() => setEditing({ id: r.id, patch: {} })}>날짜 수정</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <div className="hint">
          금액·품목은 이카운트가 정본이라 여기서 안 고칩니다. 날짜를 수기로 고치면 출처가
          <b> 수기</b>로 바뀌고, 이후 자동 재매칭이 그 줄은 건드리지 않습니다.
        </div>
      </div>
    </>
  )
}

// ---------------- 품목 마스터 (과면세·상제품) ----------------
// 사내 API의 이카운트 표에는 과면세도 상제품도 없다. 이카운트 품목정보에만 있어서 사람이 올린다.
// 손이 가는 자리라 부담을 줄이는 게 핵심이다 — 전부 다시 올리게 하지 않고,
// 마스터에 아직 없는 품목코드만 금액 큰 순으로 짚어준다. 새로 뜬 것만 채우면 된다.

function ItemMasterPane({ month, axis, onChanged, onError }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const [scope, setScope] = useState('month')

  const load = useCallback(async () => {
    try {
      const q = scope === 'month' ? `?month=${month}&axis=${axis}` : ''
      setData(await portal.get(`/api/sales/items/unregistered${q}`))
    } catch (e) { onError(e.message) }
  }, [month, axis, scope, onError])

  useEffect(() => { load() }, [load])

  const apply = async () => {
    setBusy(true)
    try {
      await portal.post('/api/sales/items/apply')
      load(); onChanged()
    } catch (e) { onError(e.message) } finally { setBusy(false) }
  }

  const setOne = async (r) => {
    const tax = window.prompt(`"${r.품목명 || r.품목코드}" 과면세 (과세 / 면세)`, '과세')
    if (tax === null || !tax.trim()) return
    const kind = window.prompt(`상제품 구분 (비워도 됩니다)`, r.품목그룹 || '')
    if (kind === null) return
    try {
      await portal.post('/api/sales/items/master', {
        품목코드: r.품목코드, 품목명: r.품목명, 과면세: tax.trim(), 상제품: kind.trim() || undefined,
      })
      load(); onChanged()
    } catch (e) { onError(e.message) }
  }

  if (!data) return <div className="panel"><div className="hint">불러오는 중…</div></div>
  const m = data.master || {}

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>품목 마스터 — 과면세 · 상제품</h3>
        <div className="row no-print">
          <select value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="month">{month} 매출에 쓰인 품목만</option>
            <option value="all">전체 기간</option>
          </select>
          <button className="btn secondary" disabled={busy} onClick={apply}>
            {busy ? '반영 중…' : '마스터 다시 입히기'}
          </button>
        </div>
      </div>
      <div className="hint">
        등록 {fmt(m.등록품목)}품목 (면세 {fmt(m.면세품목)}) · 최종 갱신 {(m.최종갱신 || '-').slice(0, 10)}
        {' · '}
        {data.미등록품목수 > 0
          ? <span className="warn">미등록 {fmt(data.미등록품목수)}품목 · {fmt(data.미등록공급가액)}원 (과세로 계산 중)</span>
          : <span className="ok">미등록 없음</span>}
      </div>
      <div className="hint">
        사내 API의 이카운트 표에는 과면세·상제품이 없어서 여기서 채웁니다. 파일은
        <b> 가져오기 탭 → 품목 마스터</b>에서 올리고, 품목코드 기준으로 <b>병합</b>되니
        전체를 다시 올려도 되고 새로 생긴 것만 올려도 됩니다. 아래 목록이 비면 다 채워진 것입니다.
      </div>

      {data.목록?.length > 0 && (
        <table>
          <thead>
            <tr><th className="l">품목코드</th><th className="l">품목명</th><th className="l">품목그룹</th>
              <th>줄수</th><th>공급가액</th><th /></tr>
          </thead>
          <tbody>
            {data.목록.map((r) => (
              <tr key={r.품목코드}>
                <td className="l">{r.품목코드}</td>
                <td className="l">{r.품목명 || '-'}</td>
                <td className="l muted">{r.품목그룹 || '-'}</td>
                <td>{fmt(r.줄수)}</td>
                <td>{fmt(r.공급가액)}</td>
                <td className="c">
                  <button className="btn tiny secondary" onClick={() => setOne(r)}>지정</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ---------------- 대사 ----------------

function ReconcilePane({ month, axis, closed, onChanged, onError }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setData(await portal.get(`/api/sales/reconcile?month=${month}&axis=${axis}`))
    } catch (e) { onError(e.message) }
  }, [month, axis, onError])

  useEffect(() => { load() }, [load])

  const rematch = async () => {
    setBusy(true)
    try {
      await portal.post('/api/sales/rematch')
      load(); onChanged()
    } catch (e) { onError(e.message) } finally { setBusy(false) }
  }

  if (!data) return <div className="panel"><div className="hint">불러오는 중…</div></div>

  return (
    <>
      <ItemMasterPane month={month} axis={axis} onChanged={onChanged} onError={onError} />

      <div className="panel">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="hint" style={{ margin: 0 }}>
            부가세자료로 회계마감일을 못 입힌 줄입니다. 결산 전에 이 목록을 비우는 게 목표고,
            <b> 미매칭</b>은 회계마감일 = 결제일로 두고 갑니다(도매 세금계산서·오프라인은 원래 그게 맞습니다).
            <b> 금액불일치</b>는 부가세자료와 이카운트의 주문 합계가 달라 확인이 필요한 줄입니다.
          </div>
          <button className="btn no-print" disabled={busy} onClick={rematch}>
            {busy ? '재매칭 중…' : '다시 매칭'}
          </button>
        </div>
        {closed && <div className="hint warn">{month}은 확정된 달이라 재매칭이 이 달은 건너뜁니다.</div>}
      </div>

      <div className="panel">
        <h3>매칭 안 된 매출줄 ({fmt(data.unmatched.length)}줄)</h3>
        {data.unmatched.length === 0 ? <div className="hint ok">모두 매칭되었습니다.</div> : (
          <table>
            <thead>
              <tr>
                <th>매칭</th><th className="l">판매처</th><th>주문번호</th><th>결제일</th>
                <th>회계마감일</th><th>출처</th><th className="l">품목명</th>
                <th>공급가액</th><th>합계금액</th>
              </tr>
            </thead>
            <tbody>
              {data.unmatched.map((r) => (
                <tr key={r.id}>
                  <td className={`c ${STATE_STYLE[r.매칭상태] || ''}`}>{r.매칭상태}</td>
                  <td className="l">{r.판매처 || '(미지정)'}</td>
                  <td>{r.주문번호 || <span className="muted">없음</span>}</td>
                  <td>{r.결제일}</td>
                  <td>{r.회계마감일 || '-'}</td>
                  <td className="c muted">{r.회계마감일_출처 || '-'}</td>
                  <td className="l">{r.품목명 || '-'}</td>
                  <td>{fmt(r.공급가액)}</td>
                  <td>{fmt(r.합계금액)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="panel">
        <h3>부가세자료에만 있는 주문 ({fmt(data.orphanVat.length)}건)</h3>
        {data.orphanVat.length === 0 ? <div className="hint ok">없습니다.</div> : (
          <>
            <div className="hint warn">
              판매처는 매출로 신고하는데 이카운트에는 없는 주문입니다 — 이카운트 매출 누락이거나
              주문번호 표기가 다른 경우입니다. 이카운트를 먼저 확인하세요.
            </div>
            <table>
              <thead>
                <tr><th className="l">판매처</th><th>주문번호</th><th>매출일</th>
                  <th>공급가액</th><th>부가세</th><th>합계금액</th></tr>
              </thead>
              <tbody>
                {data.orphanVat.map((r, i) => (
                  <tr key={`${r.판매처}|${r.주문번호}|${i}`}>
                    <td className="l">{r.판매처}</td>
                    <td>{r.주문번호}</td>
                    <td>{r.매출일}</td>
                    <td>{fmt(r.공급가액)}</td>
                    <td>{fmt(r.부가세)}</td>
                    <td>{fmt(r.합계금액)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </>
  )
}

// ---------------- 사내 API 동기화 ----------------
// 이카운트 매출도 배송일도 같은 사내 API의 다른 테이블이다. 그래서 화면도 하나로 쓴다 —
// 테이블 고르고 → 기간 열 고르고 → 미리보기로 열이 맞는지 보고 → 전량 가져오기.
// 이카운트 자사몰 매출은 한 달만 49만 줄이라 전량 가져오기는 몇 분 걸린다. 그래서 진행률을 띄운다.

const SYNC_KINDS = [
  { k: 'ecount', label: '이카운트 매출 (ecount_sales_data)' },
  { k: 'delivery', label: '배송일 보조 (ezadmin_delivery)' },
]

function ApiSyncPanel({ month, sources, onChanged, onError }) {
  const [kind, setKind] = useState('ecount')
  const [tables, setTables] = useState(null)
  const [table, setTable] = useState('')
  const [columns, setColumns] = useState([])
  const [dateCol, setDateCol] = useState('')
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [job, setJob] = useState(null)
  const ready = sources?.sp?.설정됨

  // 테이블 목록은 키가 있을 때 한 번만
  useEffect(() => {
    if (!ready) return
    portal.get('/api/sales/sync/tables')
      .then((r) => {
        setTables(r.tables || [])
        const d = r.defaults?.[kind]
        if (d) setTable(d)
      })
      .catch((e) => onError(e.message))
  }, [ready, kind, onError])

  // 테이블을 고르면 열 이름을 받아 날짜 열 후보를 채운다
  useEffect(() => {
    if (!ready || !table) { setColumns([]); return }
    setPreview(null)
    portal.get(`/api/sales/sync/columns?table=${encodeURIComponent(table)}&kind=${kind}`)
      .then((r) => {
        setColumns(r.columns || [])
        const guess = (r.columns || []).find((c) => /일자|일시|date|결제일|배송/i.test(c))
        setDateCol((d) => d || guess || '')
      })
      .catch((e) => onError(e.message))
  }, [ready, table, kind, onError])

  // 작업이 도는 동안 2초마다 진행률을 물어본다
  useEffect(() => {
    if (!job?.id || job.state !== 'running') return
    const t = setInterval(async () => {
      try {
        const r = await portal.get(`/api/sales/sync/job/${job.id}`)
        setJob(r)
        if (r.state !== 'running') { clearInterval(t); onChanged() }
      } catch (e) { clearInterval(t); onError(e.message) }
    }, 2000)
    return () => clearInterval(t)
  }, [job, onChanged, onError])

  const body = () => ({ kind, table, dateColumn: dateCol || undefined, month })

  const doPreview = async () => {
    setBusy(true)
    try { setPreview(await portal.post('/api/sales/sync/preview', body())) }
    catch (e) { onError(e.message) } finally { setBusy(false) }
  }

  const doStart = async () => {
    if (!window.confirm(`${month} ${SYNC_KINDS.find((s) => s.k === kind)?.label}을 전량 가져올까요?\n`
      + '줄 수에 따라 몇 분 걸릴 수 있습니다.')) return
    setBusy(true)
    try {
      const r = await portal.post('/api/sales/sync/start', body())
      setJob({ id: r.jobId, state: 'running', 읽은줄: 0, 페이지: 0 })
    } catch (e) { onError(e.message) } finally { setBusy(false) }
  }

  if (!ready) {
    return (
      <div className="panel">
        <h3>사내 API 연동</h3>
        <div className="hint warn">
          사내 API 키가 아직 없습니다 — <code>.env</code>에 <b>SP_API_KEY</b>를 넣어주세요.
          (앱의 관리자 &gt; DB &gt; API Keys 에서 <b>read</b> 권한으로 발급)
        </div>
        <div className="hint">
          그 전까지는 아래에서 같은 자료를 엑셀로 올려 쓰면 됩니다 — 어느 쪽으로 들어와도
          저장 모양과 매칭은 똑같습니다.
        </div>
      </div>
    )
  }

  return (
    <div className="panel">
      <h3>사내 API 연동 <span className="muted" style={{ fontSize: 12 }}>{sources.sp.base}</span></h3>
      <div className="row" style={{ marginBottom: 8 }}>
        <select value={kind} onChange={(e) => { setKind(e.target.value); setTable(''); setPreview(null) }}>
          {SYNC_KINDS.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
        </select>
        <select value={table} onChange={(e) => setTable(e.target.value)} style={{ minWidth: 220 }}>
          <option value="">테이블 선택…</option>
          {(tables || []).map((t) => {
            const name = typeof t === 'string' ? t : (t.name || t.table)
            return <option key={name} value={name}>{name}</option>
          })}
        </select>
        <select value={dateCol} onChange={(e) => setDateCol(e.target.value)} style={{ minWidth: 160 }}
          title="이 열로 기준월만 잘라 받습니다">
          <option value="">기간 열 선택…</option>
          {columns.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="btn secondary" disabled={!table || busy} onClick={doPreview}>미리보기</button>
        <button className="btn" disabled={!table || busy || job?.state === 'running'} onClick={doStart}>
          {month} 전량 가져오기
        </button>
      </div>
      <div className="hint">
        기간 열을 고르면 <b>{month}</b>만 잘라 받습니다(전체를 긁으면 훨씬 오래 걸립니다).
        사내 API 권고대로 초당 5회·순차로만 호출하므로 줄이 많으면 시간이 걸립니다.
        <br />
        <b>이카운트 매출</b>만 받으면 됩니다 — 그 표가 배송일(delivery_date)까지 같이 주기 때문에
        보통은 배송일을 따로 안 받아도 됩니다. <b>배송일 보조</b>는 배송일이 빈 줄만 메울 때 쓰고,
        그 표는 기본키가 없어 기간을 반드시 좁혀야 합니다(20만 줄 상한).
      </div>

      {job && (
        <div className={`hint ${job.state === 'error' ? 'warn' : (job.state === 'done' ? 'ok' : '')}`}>
          {job.state === 'running' && <>가져오는 중… {fmt(job.읽은줄)}줄 ({fmt(job.페이지)}쪽)</>}
          {job.state === 'done' && (
            <>완료 — {fmt(job.읽은줄)}줄 읽어 {fmt(job.넣은줄)}줄 반영, {fmt(job.건너뛴줄)}줄 건너뜀
              {job.match && <> · 재매칭 {(job.match.상태별 || [])
                .map((r) => `${r.매칭상태} ${fmt(r.건수)}줄`).join(' · ')}</>}
            </>
          )}
          {job.state === 'error' && <>실패 — {job.error}</>}
        </div>
      )}

      {preview && (
        <>
          {(preview.warnings || []).map((w, i) => <div className="hint warn" key={i}>{w}</div>)}
          <div className="hint">
            앞 {fmt(preview.rowCount)}줄 확인 · 열 매핑:{' '}
            {Object.entries(preview.mapping || {})
              .map(([f, i]) => `${f}←${preview.headers[i]}`).join(', ') || '없음'}
            {preview.missing?.length > 0 && (
              <span className="warn"> · 부족: {preview.missing.join(', ')}</span>
            )}
          </div>
          {preview.sample?.length > 0 && (
            <table>
              <thead>
                <tr>{Object.keys(preview.sample[0]).map((f) => <th key={f} className="l">{f}</th>)}</tr>
              </thead>
              <tbody>
                {preview.sample.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    {Object.keys(preview.sample[0]).map((f) => (
                      <td key={f} className="l">
                        {r[f] === null || r[f] === undefined || r[f] === ''
                          ? <span className="muted">-</span>
                          : (typeof r[f] === 'number' ? fmt(r[f], 2) : String(r[f]))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  )
}

// ---------------- 가져오기 ----------------

function ImportPane({ month, meta, batches, onChanged, onError }) {
  const fileRef = useRef(null)
  const [kind, setKind] = useState('ecount')
  const [channel, setChannel] = useState('')
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(null)
  const [mapping, setMapping] = useState({})
  const [done, setDone] = useState(null)

  const spec = meta.kinds.find((k) => k.key === kind)
  const sources = meta.sources || {}
  const reset = () => { setPreview(null); setDone(null) }

  const send = async (mode, mapOverride) => {
    if (!file) { onError('파일을 먼저 선택해주세요'); return }
    if (kind === 'vat' && !channel.trim()) { onError('부가세자료는 판매처를 지정해주세요'); return }
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('kind', kind)
      fd.append('month', month)
      if (channel.trim()) fd.append('channel', channel.trim())
      fd.append('mode', mode)
      if (mapOverride) fd.append('mapping', JSON.stringify(mapOverride))
      const res = await fetch('/api/sales/import', { method: 'POST', body: fd, credentials: 'same-origin' })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || res.statusText)
      if (mode === 'commit') {
        setDone(body); setPreview(null); setFile(null)
        if (fileRef.current) fileRef.current.value = ''
        onChanged()
      } else {
        setPreview(body); setMapping(body.mapping || {}); setDone(null)
      }
    } catch (e) { onError(e.message) } finally { setBusy(false) }
  }

  const downloadTemplate = async () => {
    try { await portal.download(`/api/sales/import/template?kind=${kind}`) } catch (e) { onError(e.message) }
  }

  const removeBatch = async (b) => {
    if (!window.confirm(`${b.파일명 || '이 가져오기'} (${fmt(b.남은건수)}줄)를 되돌릴까요?`)) return
    try {
      await portal.del(`/api/sales/batches/${b.id}`)
      onChanged()
    } catch (e) { onError(e.message) }
  }

  const missing = preview?.missing || []
  const vatChannels = (meta.channels?.부가세자료 || []).map((c) => c.판매처)

  return (
    <>
      <ApiSyncPanel month={month} sources={sources} onChanged={onChanged} onError={onError} />

      <div className="panel">
        <h3>파일로 가져오기 — 기준월 {month}</h3>
        <div className="row" style={{ marginBottom: 8 }}>
          <select value={kind} onChange={(e) => { setKind(e.target.value); reset() }} style={{ minWidth: 200 }}>
            {meta.kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
          {kind === 'vat' && (
            <input list="sales-vat-channels" placeholder="판매처 (예: 스마트스토어)" value={channel}
              onChange={(e) => setChannel(e.target.value)} style={{ width: 180 }} />
          )}
          <datalist id="sales-vat-channels">
            {vatChannels.map((c) => <option key={c} value={c} />)}
          </datalist>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); reset() }} />
          <button className="btn" disabled={!file || busy} onClick={() => send('preview')}>
            {busy ? '읽는 중…' : '미리보기'}
          </button>
          <button className="btn secondary" onClick={downloadTemplate}>양식 내려받기</button>
        </div>
        <div className="hint">
          {kind === 'ecount' && <>이카운트 매출(결제일 기준)을 그대로 올립니다. 이게 원장의 정본이 됩니다.</>}
          {kind === 'vat' && <>판매처에서 받은 부가세 신고자료입니다. 여기 적힌 <b>매출일</b>이 주문번호를 타고
            이카운트 매출줄의 <b>회계마감일</b>이 됩니다. 공급가액·부가세는 <b>일부러 자동으로 안 잡습니다</b> —
            채널 파일의 &lsquo;부가세&rsquo;는 대개 PG 수수료의 부가세라서요. 대사는 합계금액으로 합니다.</>}
          {kind === 'delivery' && <>주문번호와 배송일 두 열만 있으면 됩니다.</>}
          <br />
          열 이름은 알아서 맞추고, 못 맞춘 열은 미리보기에서 지정합니다. 같은 파일을 두 번 올려도
          중복은 걸러집니다. 필요한 열: <b>{[...(spec?.required || []),
            ...(spec?.either || []).map((g) => g.join(' 또는 '))].join(', ')}</b>
        </div>
      </div>

      {done && (
        <div className="panel">
          <h3>반영 완료</h3>
          <table style={{ maxWidth: 520 }}>
            <tbody>
              <tr><td className="l">새로 들어온 줄</td><td>{fmt(done.added)}건</td></tr>
              <tr><td className="l">중복·불량이라 건너뛴 줄</td><td>{fmt(done.skipped)}건</td></tr>
              {done.months?.length > 0 && (
                <tr><td className="l">건드린 달</td><td>{done.months.join(', ')}</td></tr>
              )}
            </tbody>
          </table>
          {done.match && (
            <div className="hint">
              재매칭 결과 —{' '}
              {(done.match.상태별 || []).map((r) => `${r.매칭상태} ${fmt(r.건수)}줄`).join(' · ')}
              {done.match.이카운트에없는부가세주문 > 0 && (
                <span className="warn">
                  {' '}· 부가세자료에만 있는 주문 {fmt(done.match.이카운트에없는부가세주문)}건
                </span>
              )}
            </div>
          )}
          {(done.warnings || []).map((w, i) => <div className="hint warn" key={i}>{w}</div>)}
        </div>
      )}

      {preview && (
        <div className="panel">
          <h3>미리보기 — {fmt(preview.rowCount)}줄 {preview.months?.length > 0 && `(${preview.months.join(', ')})`}</h3>
          {(preview.warnings || []).map((w, i) => <div className="hint warn" key={i}>{w}</div>)}

          <h3 style={{ marginTop: 14 }}>열 맞추기</h3>
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            {(spec?.fields || []).map((f) => (
              <label key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span className={(spec.required || []).includes(f) ? '' : 'muted'} style={{ width: 72 }}>{f}</span>
                <select value={mapping[f] ?? -1}
                  onChange={(e) => setMapping({ ...mapping, [f]: Number(e.target.value) })}>
                  <option value={-1}>(없음)</option>
                  {preview.headers.map((h, i) => <option key={i} value={i}>{h || `열 ${i + 1}`}</option>)}
                </select>
              </label>
            ))}
          </div>
          {missing.length > 0 && <div className="hint warn">{missing.join(', ')} 열을 지정해야 반영할 수 있습니다.</div>}

          {kind !== 'delivery' && (
            <table style={{ maxWidth: 720, marginBottom: 12 }}>
              <tbody>
                <tr>
                  <td className="l" style={{ width: 90, background: '#F8FAFC' }}>공급가액</td>
                  <td>{fmt(preview.totals.공급가액)}</td>
                  <td className="l" style={{ width: 90, background: '#F8FAFC' }}>부가세</td>
                  <td>{fmt(preview.totals.부가세)}</td>
                  <td className="l" style={{ width: 90, background: '#F8FAFC' }}>합계금액</td>
                  <td><b>{fmt(preview.totals.합계금액)}</b></td>
                </tr>
              </tbody>
            </table>
          )}

          <div className="row" style={{ marginBottom: 12 }}>
            <button className="btn secondary" disabled={busy} onClick={() => send('preview', mapping)}>열 지정 다시 읽기</button>
            <button className="btn" disabled={busy || missing.length > 0 || !preview.rowCount}
              onClick={() => send('commit', mapping)}>
              {busy ? '반영 중…' : `${fmt(preview.rowCount)}줄 반영`}
            </button>
          </div>

          <h3>앞 20줄</h3>
          <table>
            <thead>
              <tr>{(spec?.fields || []).map((f) => <th key={f} className="l">{f}</th>)}</tr>
            </thead>
            <tbody>
              {preview.sample.map((r, i) => (
                <tr key={i}>
                  {(spec?.fields || []).map((f) => (
                    <td key={f} className="l">
                      {r[f] === null || r[f] === undefined || r[f] === ''
                        ? <span className="muted">-</span>
                        : (typeof r[f] === 'number' ? fmt(r[f], 2) : r[f])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel">
        <h3>{month} 가져오기 기록</h3>
        {batches.length === 0 ? <div className="hint">아직 가져온 파일이 없습니다.</div> : (
          <table>
            <thead>
              <tr><th>종류</th><th className="l">판매처</th><th className="l">파일</th>
                <th>넣은 줄</th><th>건너뜀</th><th>남은 줄</th><th className="l">올린 사람</th><th /></tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id}>
                  <td>{meta.kinds.find((k) => k.key === b.종류)?.label || b.종류}</td>
                  <td className="l">{b.판매처 || '-'}</td>
                  <td className="l">{b.파일명 || '-'}</td>
                  <td>{fmt(b.건수)}</td>
                  <td>{fmt(b.중복건수)}</td>
                  <td>{fmt(b.남은건수)}</td>
                  <td className="l">{b.uploaded_by_name || '-'}</td>
                  <td className="c">
                    <button className="btn tiny danger" onClick={() => removeBatch(b)}>되돌리기</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="hint">
          되돌리면 그 가져오기로 들어온 줄만 지우고 곧바로 다시 매칭합니다.
        </div>
      </div>
    </>
  )
}

// ---------------- 화면 ----------------

export default function SalesRevenue({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [tab, setTab] = useState('summary')
  const [axis, setAxis] = useState(null)
  const [meta, setMeta] = useState(null)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')

  const loadMeta = useCallback(async () => {
    try {
      const m = await portal.get('/api/sales/meta')
      setMeta(m)
      setAxis((a) => a || m.defaultAxis)
    } catch (e) { setErr(e.message) }
  }, [])

  const loadSummary = useCallback(async () => {
    if (!axis) return
    try {
      setData(await portal.get(`/api/sales/summary?month=${month}&axis=${axis}`))
    } catch (e) { setErr(e.message) }
  }, [month, axis])

  useEffect(() => { loadMeta() }, [loadMeta])
  useEffect(() => { setErr(''); loadSummary() }, [loadSummary])

  const refresh = useCallback(() => { loadSummary(); loadMeta() }, [loadSummary, loadMeta])
  const closed = data?.close?.상태 === '확정'

  if (!meta || !axis || !data) {
    return (
      <>
        <h2>매출 — {month}</h2>
        {err ? <div className="error-box">{err}</div> : <div className="hint">불러오는 중…</div>}
      </>
    )
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>
          매출 — {month}
          {closed && <span className="ok" style={{ marginLeft: 8, fontSize: 12 }}>회계마감 확정</span>}
        </h2>
        <NoteDrawer target="sales-revenue" screenKey="sales"
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>

      {err && <div className="error-box">{err}</div>}

      <div className="data-tabs no-print">
        {TABS.map((t) => (
          <button key={t.k} className={`data-tab ${tab === t.k ? 'active' : ''}`} onClick={() => setTab(t.k)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'import' && <AxisPicker axes={meta.axes} axis={axis} onChange={setAxis} />}

      {tab === 'summary' && (
        <SummaryPane month={month} axes={meta.axes} axis={axis} data={data}
          canClose={meta.canClose} onChanged={refresh} onError={setErr} />
      )}
      {tab === 'ledger' && (
        <LedgerPane month={month} axis={axis} meta={meta} closed={closed}
          onChanged={refresh} onError={setErr} />
      )}
      {tab === 'reconcile' && (
        <ReconcilePane month={month} axis={axis} closed={closed}
          onChanged={refresh} onError={setErr} />
      )}
      {tab === 'import' && (
        <ImportPane month={month} meta={meta} batches={data.batches || []}
          onChanged={refresh} onError={setErr} />
      )}
    </>
  )
}
